import { Button, Flex, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { Fragment } from 'react';
import { Link, useLocation } from 'react-router';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import { useContainerQuery } from './ContainerBreakpoint';
import { BreadcrumbTerminalSlotHost } from 'src/UI/page-nav/BreadcrumbTerminalSlot';

export function AppBreadcrumbs() {
  const location = useLocation();
  const { pathname: path, search } = location;
  const { clientId, client } = useCurrentClientIfAvailable();
  const { flagEnabledIndividualDetailsRework, flagEnabledResponsiveMenus } = useFlags();
  const cq = useContainerQuery();
  const isCompact = cq({ base: true, xs: false });

  const onClientPage = clientId != null && path.startsWith(`/client/${clientId}`);
  // The cashflow model page is a full-bleed takeover with its own breadcrumb;
  // a second trail above it would sit on the page's own background. It lives
  // at /client/:id/cashflows/model/:scenarioId and the same under /family.
  const [, owner, , section, page, scenarioId] = path.split('/');
  const onCashflowModelPage =
    (owner === 'client' || owner === 'family') &&
    section === 'cashflows' &&
    page === 'model' &&
    Boolean(scenarioId);
  if (onCashflowModelPage) {
    return null;
  }
  if (flagEnabledIndividualDetailsRework && onClientPage) {
    // Hide the breadcrumb trail on the reworked client pages. Only when the tabs collapse on narrow
    // viewports do we keep the terminal slot mounted, so the collapsed mobile tabs (which portal
    // into it via integrateBreadcrumb) still render. On wider viewports render nothing — an empty
    // host would add a stray line of vertical space above the content.
    if (flagEnabledResponsiveMenus && isCompact) {
      return <BreadcrumbTerminalSlotHost />;
    }
    return null;
  }

  const items = buildBreadcrumbItems(path, search, clientId, client ?? undefined, isCompact);

  if (items.length === 0) {
    return null;
  }

  return (
    <Flex
      align="center"
      wrap="wrap"
      gap="xs"
      mx={cq({ base: 'xs', xl: 'sm' })}
      mb={cq({ base: 'xs', xl: 'md' })}
      mt={cq<string | number>({ base: 0, md: 'xs', lg: 'sm', xl: 'md' })}
      style={{ minWidth: 0 }}
    >
      {items.map((item, i) => {
        const isCurrent = i === items.length - 1;
        const textProps = isCurrent
          ? {
              c: 'gray.10',
              fw: 700,
            }
          : {
              c: 'gray.9',
              fw: 500,
            };

        if (item.highlight) {
          textProps.c = 'brand.6';
        }

        return (
          <Fragment key={item.href}>
            <Button component={Link} variant="text" to={item.href}>
              <Text size="md" {...textProps}>
                {item.label}
              </Text>
            </Button>
            {i < items.length - 1 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'var(--mantine-color-gray-7)',
                }}
              >
                <IconChevronRight size={16} />
              </span>
            )}
          </Fragment>
        );
      })}
      <BreadcrumbTerminalSlotHost />
    </Flex>
  );
}

function buildBreadcrumbItems(
  path: string,
  search: string,
  clientId: string | undefined,
  client: { firstName?: string; lastName?: string } | undefined,
  isCompact: boolean,
) {
  if (
    path.includes('financial-items/') ||
    path.includes('financial-data/create') ||
    path.includes('file-upload') ||
    path.match(/\/client\/[^/]+\/suitability-report$/) ||
    path.match(/\/client\/[^/]+\/meeting-agenda$/) ||
    path.match(/\/client\/[^/]+\/data-export$/) ||
    path.match(/\/client\/[^/]+\/meeting-note$/) ||
    path.match(/\/client\/[^/]+\/document\/[^/]+\/[^/]+\/edit$/)
  ) {
    return [];
  }

  const parts: { label: string | null; href: string; highlight?: boolean }[] = [];

  if (clientId != null && path.startsWith(`/client/${clientId}`)) {
    parts.push(
      { label: isCompact ? 'Clients' : 'All clients', href: '/clients/all' },
      {
        label: formatName(
          client?.firstName,
          client?.lastName,
          isCompact ? NameFormat.PROFESSIONAL_SHORT : NameFormat.PROFESSIONAL,
        ),
        href: `/client/${clientId}/non-financial-data`,
        highlight: true,
      },
    );
  }

  if (search.includes('fileId') && clientId != null) {
    parts.push({
      label: 'Documents',
      href: `/client/${clientId}/document/add`,
    });
  }

  if (search.includes('fileName')) {
    const fileName = new URLSearchParams(search).get('fileName');
    parts.push({ label: fileName, href: window.location.href });
  }

  return parts;
}
